import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, Save, AlertTriangle, Crown, Sparkles } from 'lucide-react'
import { api, type UserSettings } from '@/lib/api'
import { useAuth } from '@/hooks/use-auth'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'

// Convert between local hours and UTC hours using the browser's timezone offset
const offsetMinutes = new Date().getTimezoneOffset() // e.g. -120 for UTC+2
function localHourFromUtc(utcHour: number): number {
  return ((utcHour - offsetMinutes / 60) + 24) % 24
}
function utcHourFromLocal(localHour: number): number {
  return ((localHour + offsetMinutes / 60) + 24) % 24
}

const PLAN_LABELS: Record<string, { label: string; color: 'secondary' | 'default' | 'success' }> = {
  free: { label: 'Free', color: 'secondary' },
  plus: { label: 'Plus', color: 'default' },
  max: { label: 'Max', color: 'success' },
}

export function SettingsPage() {
  const { user } = useAuth()
  const [settings, setSettings] = useState<UserSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [justUpgraded, setJustUpgraded] = useState(false)

  const plan = user?.billing_plan || 'free'
  const isPaid = plan !== 'free'
  const planInfo = PLAN_LABELS[plan] || PLAN_LABELS.free

  const cycleStart = user?.billing_cycle_start ? new Date(user.billing_cycle_start) : null
  const cycleEnd = user?.billing_cycle_end ? new Date(new Date(user.billing_cycle_end).getTime() - 86400000) : null
  const formatDate = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('upgraded') === 'true') {
      setJustUpgraded(true)
      // Clean URL
      window.history.replaceState({}, '', '/settings')
      setTimeout(() => setJustUpgraded(false), 5000)
    }
  }, [])

  useEffect(() => {
    api.getSettings()
      .then(({ settings }) => setSettings(settings))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  const handleSave = async () => {
    if (!settings) return
    setSaving(true)
    try {
      await api.updateSettings(settings)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (err) {
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  const handleManageBilling = async () => {
    try {
      const { updatePaymentMethod, cancel } = await api.getBillingPortal()
      // Paddle provides a customer portal URL — open it in a new tab
      const portalUrl = updatePaymentMethod || cancel
      if (portalUrl) {
        window.open(portalUrl, '_blank')
      }
    } catch (err) {
      console.error(err)
    }
  }

  const handleDisconnect = async () => {
    if (!confirm('This will unfollow all channels and disconnect your Google account. Continue?')) return
    setDisconnecting(true)
    try {
      await api.disconnect()
      window.location.href = '/'
    } catch (err) {
      console.error(err)
      setDisconnecting(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-48 w-full rounded-xl" />
      </div>
    )
  }

  if (!settings) return null

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Settings</h1>

      {justUpgraded && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
          Plan upgraded successfully! Your new features are now active.
        </div>
      )}

      {/* Plan */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                Your Plan
                <Badge variant={planInfo.color}>{planInfo.label}</Badge>
              </CardTitle>
              <CardDescription>
                {plan === 'free' && '15 summaries/month. Upgrade for more summaries, daily digest, and upcoming features.'}
                {plan === 'plus' && '150 summaries/month with daily digest emails.'}
                {plan === 'max' && '500 summaries/month with all features.'}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {cycleStart && cycleEnd && (
            <p className="text-sm text-muted-foreground">
              Current billing cycle: {formatDate(cycleStart)} to {formatDate(cycleEnd)}
            </p>
          )}
          {isPaid ? (
            <Button variant="outline" size="sm" onClick={handleManageBilling} className="gap-1.5">
              <Crown className="h-4 w-4" />
              Manage Billing
            </Button>
          ) : (
            <Link to="/upgrade">
              <Button size="sm" className="gap-1.5">
                <Sparkles className="h-4 w-4" />
                Upgrade Plan
              </Button>
            </Link>
          )}
        </CardContent>
      </Card>

      {/* Digest settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Daily Digest</CardTitle>
          <CardDescription>Get a morning email with all your new summaries.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Enable digest emails</p>
              <p className="text-xs text-muted-foreground">One email per day, never more.</p>
            </div>
            <Switch
              checked={settings.digest_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, digest_enabled: checked })}
            />
          </div>

          {settings.digest_enabled && (
            <div>
              <label className="text-sm font-medium">Delivery time (your local time)</label>
              <select
                value={localHourFromUtc(settings.digest_hour_local)}
                onChange={(e) => setSettings({ ...settings, digest_hour_local: utcHourFromLocal(parseInt(e.target.value)) })}
                className="mt-1 block w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
              >
                {Array.from({ length: 24 }).map((_, h) => (
                  <option key={h} value={h}>
                    {h === 0 ? '12:00 AM' : h < 12 ? `${h}:00 AM` : h === 12 ? '12:00 PM' : `${h - 12}:00 PM`}
                  </option>
                ))}
              </select>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Processing filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Minimum video length</CardTitle>
          <CardDescription>
            Skip short videos like YouTube Shorts so they don't use up your summary quota. Videos below this length won't be summarized or shown in your feed.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <label className="text-sm font-medium">Don't summarize videos shorter than</label>
          <select
            value={settings.min_video_duration_seconds}
            onChange={(e) => setSettings({ ...settings, min_video_duration_seconds: parseInt(e.target.value) })}
            className="mt-1 block w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
          >
            <option value={0}>No minimum (summarize everything)</option>
            <option value={60}>1 minute</option>
            <option value={120}>2 minutes</option>
            <option value={180}>3 minutes</option>
            <option value={300}>5 minutes</option>
            <option value={600}>10 minutes</option>
            <option value={900}>15 minutes</option>
          </select>
        </CardContent>
      </Card>

      {/* Save */}
      <Button onClick={handleSave} disabled={saving} className="gap-2">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        {saved ? 'Saved!' : 'Save settings'}
      </Button>

      {/* Danger zone */}
      <Card className="border-destructive/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base text-destructive">
            <AlertTriangle className="h-4 w-4" />
            Danger zone
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-4 text-sm text-muted-foreground">
            Disconnect your Google account and stop all future processing. Your data will remain but no new summaries will be generated.
          </p>
          <Button variant="destructive" onClick={handleDisconnect} disabled={disconnecting}>
            {disconnecting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Disconnect Google Account
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
